import { Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from 'src/app/services/swal.service';
import { IPatient } from 'src/app/interfaces/IPatient';
import { ISearchPatient } from 'src/app/interfaces/ISearchPatient';
import { AllergyService } from 'src/app/services/allergy.service';
import { CarePlanService } from 'src/app/services/carePlan.service';
import { CurrentMedicationService } from 'src/app/services/currentMedication.service';
import { CurrentProblemsService } from 'src/app/services/currentProblems.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { HysteroscopyFileService } from 'src/app/services/hyseroscopyFile.service';
import { ImagingService } from 'src/app/services/imaging.service';
import { LaboratoryFileService } from 'src/app/services/laboratoryFile.service';
import { MedicalAlertService } from 'src/app/services/medicalAlert.service';
import { MedicalDeviceService } from 'src/app/services/medicalDevice.service';
import { MedicalHistoryService } from 'src/app/services/medicalHistory.service';
import { PastMedicationService } from 'src/app/services/pastMedication.service';
import { PatientService } from 'src/app/services/patient.service';
import { PregnancyHistoryService } from 'src/app/services/pregnancyHistory.service';
import { PregnancyOutcomeService } from 'src/app/services/pregnancyOutcome.service';
import { PregnancyStatusService } from 'src/app/services/pregnancyStatus.service';
import { ProcedureService } from 'src/app/services/procedure.service';
import { ResolvedProblemsService } from 'src/app/services/resolvedProblems.service';
import { SocialHistoryService } from 'src/app/services/socialHistory.service';
import { TravelHistoryService } from 'src/app/services/travelHistory.service';
import { VaccinationService } from 'src/app/services/vaccination.service';
import { VitalSignService } from 'src/app/services/vitalSign.service';
import Swal from 'sweetalert2';
import { IGetVisit } from 'src/app/interfaces/IGetVisit';
import { IInpatientGOC } from 'src/app/interfaces/IInpatient';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { SelfReportService } from 'src/app/services/selfReport.service';
import moment from 'moment';
import { date_format } from 'src/date/date.configuration';
import { EMPTY, Subject, catchError, switchMap, takeUntil } from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
    selector: 'app-patients-goc',
    templateUrl: './patients-goc.component.html',
    styleUrls: ['./patients-goc.component.css'],
    standalone: false
})
export class PatientsGocComponent {
  private readonly destroy$ = new Subject<void>();
  // @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  @ViewChild('visitModalRef', { static: true }) visitModalRef!: NzModalRef;

  public documentID?: string;
  public firstName?: string;
  public lastName?: string;
  public dateOfBirth?: Date;

  // DUPLICATE VARIABLES FOR SEARCH FIELDS
  // SET THE VALUE ON PRESS OF BUTTON "SEARCH"
  //
  // IF WE DON'T HAVE THOSE THE SEARCH WILL GET
  // THE VALUES FROM THE SEARCH FORM WITHOUT THE
  // NEED TO PRESS THE SEARCH BUTTON
  // AND THE PAGINATION WILL BREAK
  public documentValue?: string;
  public firstNameValue?: string;
  public lastNameValue?: string;
  public dateOfBirthValue?: Date;

  public data: MatTableDataSource<IPatient> = new MatTableDataSource<IPatient>(
    []
  );

  public totalRecords: number = 0;

  public displayedColumns: string[] = [
    'documentNumber',
    'firstName',
    'lastName',
    'dateOfBirth',
    'actions',
  ];

  isLoading: boolean = false;
  isNewVisitLoading: boolean = false;

  isVisitLoading = false;
  isVisitVisible = false;
  isNewSelected = true;

  selectedPatientFullName: string = '';
  selectedPatientID?: number;
  selectedEOC?: number;
  draftVisit?: number;

  existingVisits: IGetVisit[] | null = null;

  ngAfterViewInit() {
    this.data.paginator = this.paginator;
  }

  constructor(
    private readonly patientService: PatientService,
    private readonly router: Router,
    private readonly allergyService: AllergyService,
    private readonly vaccinationService: VaccinationService,
    private readonly medicalAlertService: MedicalAlertService,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly procedureService: ProcedureService,
    private readonly medicalDeviceService: MedicalDeviceService,
    private readonly currentProblemsService: CurrentProblemsService,
    private readonly resolvedProblemsService: ResolvedProblemsService,
    private readonly pregnancyHistoryService: PregnancyHistoryService,
    private readonly pregnancyOutcomeService: PregnancyOutcomeService,
    private readonly pregnancyStatusService: PregnancyStatusService,
    private readonly carePlanService: CarePlanService,
    private readonly socialHistoryService: SocialHistoryService,
    private readonly travelHistoryService: TravelHistoryService,
    private readonly currentMedicationService: CurrentMedicationService,
    private readonly pastMedicationService: PastMedicationService,
    private readonly imagingService: ImagingService,
    private readonly labService: LaboratoryFileService,
    private readonly hysteroscopyFileService: HysteroscopyFileService,
    private readonly vitalSignService: VitalSignService,
    private readonly eocService: EpisodeOfCareService,
    private readonly selfReportService: SelfReportService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly localeService: LocaleService,
  ) {}

  registerPatient() {
    this.router.navigate(['/ehr/register-patient']);
  }

  clearPatientsForm() {
    this.documentID = undefined;
    this.firstName = undefined;
    this.lastName = undefined;
    this.dateOfBirth = undefined;
    this.data = new MatTableDataSource<IPatient>([]);
    this.data.paginator = this.paginator;
  }

  // RESET DATA  WHEN ACCESSING A PATIENT
  clearDataFromServices() {
    this.allergyService.allergyDataSub.next({});
    this.vaccinationService.vaccinationDataSub.next([]);
    this.medicalAlertService.medicalAlertDataSub.next([]);
    this.medicalHistoryService.medicalHistoryDataSub.next([]);
    this.procedureService.procedureDataSub.next({});
    this.medicalDeviceService.medicalDeviceDataSub.next({});
    this.currentProblemsService.currentProblemDataSub.next({});
    this.resolvedProblemsService.resolvedProblemDataSub.next([]);
    this.pregnancyHistoryService.pregnancyHistoryDataSub.next([]);
    this.pregnancyOutcomeService.pregnancyOutcomeDataSub.next([]);
    this.pregnancyStatusService.pregnancyStatusDataSub.next([]);
    this.carePlanService.carePlanDataSub.next([]);
    this.socialHistoryService.socialHistoryDataSub.next([]);
    this.travelHistoryService.travelHistoryDataSub.next([]);
    this.currentMedicationService.currentMedicationDataSub.next({});
    this.pastMedicationService.pastMedicationDataSub.next([]);
    this.imagingService.imagingDataSub.next([]);
    this.labService.laboratoryDataSub.next([]);
    this.hysteroscopyFileService.hysteroscopyDataSub.next([]);
    this.vitalSignService.vitalSignDataSub.next([]);
    this.selfReportService.selfReportDataSub.next([]);
  }

  onRowClicked(row: IPatient) {
    // RESET DATA  BEFORE ACCESSING A PATIENT
    this.clearDataFromServices();

    this.router.navigate(['/ehr/profile/' + row?.id]);
  }

  async pageChange() {
    const patientObj: ISearchPatient = {};
    if (this?.documentValue) patientObj.documentNumber = this.documentValue;
    if (this?.firstNameValue) patientObj.firstName = this.firstNameValue;
    if (this?.lastNameValue) patientObj.lastName = this.lastNameValue;
    if (this?.dateOfBirthValue)
      patientObj.dateOfBirth = this.dateOfBirthValue!.toString();

    await this.patientService
      .getPatientsGOC(
        patientObj,
        this.paginator.pageSize,
        this.paginator.pageIndex + 1
      )
      .subscribe({
        next: (d) => {
          this.data = new MatTableDataSource(d?.data?.map((d: IPatient) => d));

          this.totalRecords = d.totalRecords || 0;
        },
        error: (err) => {},
      });
  }

  async searchPatientsForm() {
    this.paginator.pageIndex = 0;

    this.documentValue = this.documentID;
    this.firstNameValue = this.firstName;
    this.lastNameValue = this.lastName;
    this.dateOfBirthValue = this.dateOfBirth;

    this.pageChange();
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  // TO REMOVE LATER
  ngOnInit() {
    // this.searchPatientsForm();
    this.localeService.locale$
      .pipe(
        switchMap(() =>
          this.isVisitVisible && this.selectedEOC
            ? this.eocService
                .getDraftVisitFromEOC(this.selectedEOC, true)
                .pipe(catchError(() => EMPTY))
            : EMPTY,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.existingVisits = response.data ?? [];
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openVisit(item: IInpatientGOC) {
    this.isLoading = true;
    if (item?.episodeCareId) {
      this.eocService.getDraftVisitFromEOC(item.episodeCareId, true).subscribe({
        next: (res) => {
          this.existingVisits = res?.data;
          this.selectedPatientFullName = item.firstName + ' ' + item.lastName;
          this.selectedPatientID = item.id;
          this.selectedEOC = item.episodeCareId;
          this.isVisitVisible = true;
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
        },
      });
    } else {
      this.eocService.createNewVisitNoEOC(item.id?.toString()).subscribe({
        next: (res) => {
          res?.data?.episodeOfCareId?.toString() &&
            this.eocService.currentEocId.set(
              res?.data?.episodeOfCareId?.toString()
            );

          res?.data?.visitId?.toString() &&
            this.eocService.currentVisitId.set(res?.data?.visitId?.toString());

          this.patientService.patientIDsignal.set(item.id?.toString()!);

          this.router.navigate([
            '/ehr/inPatientView',
            this.patientService.patientIDsignal(),
            this.eocService.currentEocId(),
            this.eocService.currentVisitId(),
          ]);

          this.resetVisitCancel();

          this.visitModalRef.destroy('success');
          this.isLoading = false;
        },
        error: (err) => {
          this.swalToastService.toastError('Unable to create Episode of Care');
          this.isLoading = false;
        },
      });
    }
  }

  handleView(item: IPatient) {
    if (item?.id) {
      // REQUEST NEW VISIT AND ASSIGN VISIT ID TO EOC SERVICE THEN NAVIGATE
      // this.eocService.currentVisitId.set(res?.data?.visitId?.toString());
      // this.router.navigate(['/inPatientView']);
      Swal.fire({
        title: `<span style="font-size: 20px;">${this.translate.instant(
          'Create New Visit'
        )}</span> <span style="font-size: 16px; font-weight: normal"></span>`,
        html: `${this.translate.instant(
          'Are you sure that you want to create a new visit for'
        )} <span style="font-weight: bold;">${
          item?.firstName + ' ' + item?.lastName
        }</span> ?`,
        showDenyButton: false,
        showCancelButton: true,
        cancelButtonText: `${this.translate.instant('Cancel')}`,
        confirmButtonText: `${this.translate.instant('Ok')}`,
      }).then((result) => {
        if (result.isConfirmed) {
          this.eocService.createNewVisitNoEOC(item?.id).subscribe({
            next: (res) => {
              res?.data?.episodeOfCareId?.toString() &&
                this.eocService.currentEocId.set(
                  res?.data?.episodeOfCareId?.toString()
                );

              res?.data?.visitId?.toString() &&
                this.eocService.currentVisitId.set(
                  res?.data?.visitId?.toString()
                );

              this.patientService.patientIDsignal.set(item?.id?.toString()!);

              // RESET DATA  BEFORE ACCESSING A PATIENT
              this.clearDataFromServices();

              this.router.navigate([
                '/ehr/inPatientView',
                this.patientService.patientIDsignal(),
                this.eocService.currentEocId(),
                this.eocService.currentVisitId(),
              ]);

              this.swalToastService.toastSuccess(
                'New Visit created successfully'
              );
            },

            error: (err) => {
              this.swalToastService.toastError('Unable to create New Visit');
            },
          });
        }
      });
    }
  }

  handleVisitSubmit() {
    if (
      !this.isNewSelected &&
      this.draftVisit &&
      this.selectedEOC &&
      this.selectedPatientID
    ) {
      this.selectedEOC &&
        this.eocService.currentEocId.set(this.selectedEOC.toString());

      this.draftVisit &&
        this.eocService.currentVisitId.set(this.draftVisit.toString());

      this.patientService.patientIDsignal.set(
        this.selectedPatientID.toString()
      );

      this.visitModalRef.afterClose.subscribe((result) => {
        if (result === 'success') {
          this.router.navigate([
            '/ehr/inPatientView',
            this.patientService.patientIDsignal(),
            this.eocService.currentEocId(),
            this.eocService.currentVisitId(),
          ]);

          this.resetVisitCancel();
        }
      });
      this.visitModalRef.destroy('success');
    } else if (
      this.isNewSelected &&
      this.selectedEOC &&
      this.selectedPatientID
    ) {
      this.eocService.createNewVisit(this.selectedEOC).subscribe({
        next: (res) => {
          res?.data?.episodeOfCareId?.toString() &&
            this.eocService.currentEocId.set(
              res?.data?.episodeOfCareId?.toString()
            );

          res?.data?.visitId?.toString() &&
            this.eocService.currentVisitId.set(res?.data?.visitId?.toString());

          this.patientService.patientIDsignal.set(
            this.selectedPatientID?.toString()!
          );

          this.visitModalRef.afterClose.subscribe((result) => {
            if (result === 'success') {
              this.router.navigate([
                '/ehr/inPatientView',
                this.patientService.patientIDsignal(),
                this.eocService.currentEocId(),
                this.eocService.currentVisitId(),
              ]);

              this.resetVisitCancel();
            }
          });
          this.visitModalRef.destroy('success');

          this.swalToastService.toastSuccess('New Visit created successfully');
        },
        error: (err) => {
          this.swalToastService.toastError('Unable to create New Visit');
        },
      });
    }
  }

  resetVisitCancel() {
    this.isNewSelected = true;
    this.isVisitVisible = false;
    this.draftVisit = undefined;
    this.selectedEOC = undefined;
    this.selectedPatientID = undefined;
    this.clearDataFromServices();
  }

  handleVisitCancel() {
    this.resetVisitCancel();

    this.visitModalRef.destroy('cancel');
  }

  selectNewVisit() {
    this.isNewSelected = true;
  }

  selectDraftVisit() {
    this.isNewSelected = false;
  }

  formatDate(date?: string) {
    return date ? moment(date).format(date_format) : '';
  }
}
